import { InstanceType, DeploymentStage } from '../types/wordpress.type';

export const wordpressInstanceTypes: InstanceType[] = [
  {
    id: 'wp-small',
    name: 'Small',
    cpu: '1 vCPU',
    ram: '1 GB',
    price: '$15/mo',
    category: 'small',
    description: 'Perfect for personal blogs',
    recommended: 'Up to 10K visits/month'
  },
  {
    id: 'wp-medium',
    name: 'Medium',
    cpu: '2 vCPUs',
    ram: '4 GB',
    price: '$45/mo',
    category: 'medium',
    description: 'Great for business sites',
    recommended: 'Up to 100K visits/month'
  },
  {
    id: 'wp-large',
    name: 'Large',
    cpu: '4 vCPUs',
    ram: '8 GB',
    price: '$95/mo',
    category: 'large',
    description: 'For e-commerce and high traffic',
    recommended: 'Up to 500K visits/month'
  },
  {
    id: 'wp-enterprise',
    name: 'Enterprise',
    cpu: '8 vCPUs',
    ram: '16 GB',
    price: '$195/mo',
    category: 'enterprise',
    description: 'Maximum performance',
    recommended: '1M+ visits/month'
  },
];

export const phpVersions = [
  { id: 'php-8.2', name: 'PHP 8.2', recommended: true },
  { id: 'php-8.1', name: 'PHP 8.1' },
  { id: 'php-8.0', name: 'PHP 8.0' },
  { id: 'php-7.4', name: 'PHP 7.4' },
];

export const wpVersions = [
  { id: 'wp-latest', name: 'Latest (6.4)', recommended: true },
  { id: 'wp-6.3', name: 'WordPress 6.3' },
  { id: 'wp-6.2', name: 'WordPress 6.2' },
];

export const environments = [
  { id: 'production', name: 'Production', icon: '🚀', color: 'from-green-500 to-emerald-600' },
  { id: 'staging', name: 'Staging', icon: '🔨', color: 'from-yellow-500 to-orange-500' },
  { id: 'development', name: 'Development', icon: '💻', color: 'from-blue-500 to-cyan-500' },
];

export const createDeploymentStages = (): DeploymentStage[] => [
  {
    id: 1,
    name: 'Validating Configuration',
    status: 'pending',
    duration: 2,
    substeps: [
      { id: '1-1', name: 'Checking domain availability', status: 'pending' },
      { id: '1-2', name: 'Validating instance type', status: 'pending' },
      { id: '1-3', name: 'Verifying resource quotas', status: 'pending' },
    ],
    logs: [],
    expandable: true,
    expanded: false,
  },
  {
    id: 2,
    name: 'Creating Compute Resources',
    status: 'pending',
    duration: 15,
    substeps: [
      { id: '2-1', name: 'Allocating EC2 instance', status: 'pending' },
      { id: '2-2', name: 'Configuring networking', status: 'pending' },
      { id: '2-3', name: 'Setting up security groups', status: 'pending' },
      { id: '2-4', name: 'Attaching storage volumes', status: 'pending' },
    ],
    logs: [],
    expandable: true,
    expanded: false,
  },
  {
    id: 3,
    name: 'Provisioning Database',
    status: 'pending',
    duration: 24,
    substeps: [
      { id: '3-1', name: 'Creating RDS MySQL instance', status: 'pending' },
      { id: '3-2', name: 'Configuring database parameters', status: 'pending' },
      { id: '3-3', name: 'Setting up security rules', status: 'pending' },
      { id: '3-4', name: 'Testing database connection', status: 'pending' },
    ],
    logs: [],
    expandable: true,
    expanded: false,
  },
  {
    id: 4,
    name: 'Installing WordPress Core',
    status: 'pending',
    duration: 18,
    substeps: [
      { id: '4-1', name: 'Downloading WordPress', status: 'pending' },
      { id: '4-2', name: 'Extracting files', status: 'pending' },
      { id: '4-3', name: 'Configuring wp-config.php', status: 'pending' },
      { id: '4-4', name: 'Running installation', status: 'pending' },
      { id: '4-5', name: 'Creating admin user', status: 'pending' },
    ],
    logs: [],
    expandable: true,
    expanded: false,
  },
  {
    id: 5,
    name: 'Configuring Web Server',
    status: 'pending',
    duration: 12,
    substeps: [
      { id: '5-1', name: 'Installing Nginx', status: 'pending' },
      { id: '5-2', name: 'Configuring PHP-FPM', status: 'pending' },
      { id: '5-3', name: 'Setting up virtual host', status: 'pending' },
      { id: '5-4', name: 'Optimizing performance', status: 'pending' },
    ],
    logs: [],
    expandable: true,
    expanded: false,
  },
  {
    id: 6,
    name: 'Setting up SSL Certificate',
    status: 'pending',
    duration: 8,
    substeps: [
      { id: '6-1', name: 'Requesting Let\'s Encrypt certificate', status: 'pending' },
      { id: '6-2', name: 'Configuring HTTPS', status: 'pending' },
      { id: '6-3', name: 'Setting up auto-renewal', status: 'pending' },
    ],
    logs: [],
    expandable: true,
    expanded: false,
  },
  {
    id: 7,
    name: 'Configuring CDN',
    status: 'pending',
    duration: 10,
    substeps: [
      { id: '7-1', name: 'Creating CloudFront distribution', status: 'pending' },
      { id: '7-2', name: 'Configuring cache behaviors', status: 'pending' },
      { id: '7-3', name: 'Setting up origin', status: 'pending' },
    ],
    logs: [],
    expandable: true,
    expanded: false,
  },
  {
    id: 8,
    name: 'Security Hardening',
    status: 'pending',
    duration: 7,
    substeps: [
      { id: '8-1', name: 'Installing security plugins', status: 'pending' },
      { id: '8-2', name: 'Configuring firewall rules', status: 'pending' },
      { id: '8-3', name: 'Setting up DDoS protection', status: 'pending' },
      { id: '8-4', name: 'Hardening file permissions', status: 'pending' },
    ],
    logs: [],
    expandable: true,
    expanded: false,
  },
  {
    id: 9,
    name: 'Running Health Checks',
    status: 'pending',
    duration: 5,
    substeps: [
      { id: '9-1', name: 'Testing web server', status: 'pending' },
      { id: '9-2', name: 'Verifying database connection', status: 'pending' },
      { id: '9-3', name: 'Checking SSL certificate', status: 'pending' },
      { id: '9-4', name: 'Running performance tests', status: 'pending' },
    ],
    logs: [],
    expandable: true,
    expanded: false,
  },
  {
    id: 10,
    name: 'Finalizing Deployment',
    status: 'pending',
    duration: 3,
    substeps: [
      { id: '10-1', name: 'Generating site credentials', status: 'pending' },
      { id: '10-2', name: 'Sending welcome email', status: 'pending' },
      { id: '10-3', name: 'Deployment complete', status: 'pending' },
    ],
    logs: [],
    expandable: true,
    expanded: false,
  },
];

export const eventTemplates = {
  info: [
    'Initializing deployment process',
    'Allocating compute resources',
    'Installing system dependencies',
    'Configuring web server',
    'Testing database connection',
    'Downloading WordPress core files',
    'Extracting installation files',
    'Running database migrations',
    'Installing security updates',
    'Configuring cache settings',
  ],
  warn: [
    'High CPU usage detected (78%)',
    'Memory usage above 70%',
    'Network latency increased to 150ms',
    'Disk I/O operations pending',
    'SSL certificate will expire in 30 days',
  ],
  success: [
    'WordPress core installed successfully',
    'SSL certificate issued and installed',
    'Database initialized successfully',
    'Security plugins activated',
    'CDN configuration complete',
    'All health checks passed',
  ],
  error: [
    'Failed to connect to database',
    'SSL certificate request failed',
    'Insufficient disk space',
    'Network connection timeout',
  ],
};